import { NumericFormat } from 'react-number-format';
import { Col, Row } from 'antd';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  type TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { getStyleVariableValue, truncateText } from '../../../../utilz';

// Sample data
type GrossProductData = {
  carrierName: string;
  claimCounts: number;
  totalPaid: number;
  currencyCode: string;
};

const GROSS_PRODUCT_SAMPLE_DATA: GrossProductData[] = [
  {
    carrierName: 'A A A SIGN TEST CARRIER',
    claimCounts: 803,
    currencyCode: 'USD',
    totalPaid: 4322,
  },
  {
    carrierName: 'A AND A DELIVERY',
    claimCounts: 4212,
    currencyCode: 'USD',
    totalPaid: 3332,
  },
  {
    carrierName: 'A DUIE PYLE INCORPORATED',
    claimCounts: 1452,
    currencyCode: 'USD',
    totalPaid: 4529,
  },
  {
    carrierName: 'AAA COOPER TRANSPORTATION',
    claimCounts: 112,
    currencyCode: 'USD',
    totalPaid: 3242,
  },
  {
    carrierName: 'ARTURO TRANSPORTACION Y COMERCIALIZACION',
    claimCounts: 290,
    currencyCode: 'USD',
    totalPaid: 294,
  },
  {
    carrierName: 'AVERITT EXPRESS INCORPORATED',
    claimCounts: 290,
    currencyCode: 'USD',
    totalPaid: 4212,
  },
  {
    carrierName: 'CEVA FREIGHT HOLDINGS MALAYSIA SDN BHD',
    claimCounts: 32,
    currencyCode: 'USD',
    totalPaid: 4312,
  },
  {
    carrierName: 'UNITED PARCEL SERVICE SPAIN',
    claimCounts: 290,
    currencyCode: 'USD',
    totalPaid: 1234,
  },
];

const renderLegendText = (value: string) => {
  return (
    <span
      style={{
        color: getStyleVariableValue('--disabled-text'),
        lineHeight: '12px',
        paddingLeft: '3px',
        verticalAlign: 'middle',
      }}
    >
      {value}
    </span>
  );
};

const CustomTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
  const tooltipData: GrossProductData = payload?.[0]?.payload;

  if (active && tooltipData) {
    const { carrierName, claimCounts, totalPaid, currencyCode } = tooltipData;

    return (
      <Row className="state-tooltip px-16 py-10 d-flex flex-direction-column" align="middle">
        <Col>
          <h4 className="text-white mb-5">{carrierName}</h4>
        </Col>
        <Col>
          <table>
            <tbody>
              <tr>
                <td className="py-2 px-5">
                  <div className="text-info">Claim Number Count:</div>
                </td>
                <td className="py-2 px-5">
                  <div className="text-info">{claimCounts}</div>
                </td>
              </tr>
              <tr>
                <td className="py-2 px-5">
                  <div className="text-info">Claim Payments Sum:</div>
                </td>
                <td className="py-2 px-5">
                  <div className="text-info">
                    <NumericFormat
                      thousandsGroupStyle="thousand"
                      displayType="text"
                      value={String(totalPaid)}
                      thousandSeparator
                      fixedDecimalScale
                      decimalScale={2}
                      decimalSeparator="."
                    />
                    <span className="ml-5">{currencyCode}</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </Col>
      </Row>
    );
  }

  return null;
};

const StackedBar = () => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={GROSS_PRODUCT_SAMPLE_DATA} barGap={0}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="carrierName"
          tick={{
            fill: getStyleVariableValue('--disabled-text'),
            fontSize: 10,
            fontWeight: '600',
          }}
          tickMargin={8}
          tickFormatter={(value) => truncateText(value, 10)}
          interval="equidistantPreserveStart"
        />
        <YAxis
          tick={{
            fill: getStyleVariableValue('--disabled-text'),
            fontSize: 12,
          }}
          width={30}
        />
        <Tooltip
          content={<CustomTooltip />}
          wrapperStyle={{
            backgroundColor: getStyleVariableValue('--dark'),
            border: 0,
            whiteSpace: 'nowrap',
            zIndex: 10,
          }}
        />
        <Legend
          verticalAlign="bottom"
          align="center"
          wrapperStyle={{
            fontSize: 12,
          }}
          formatter={renderLegendText}
          iconType="square"
          iconSize={12}
        />
        <Bar dataKey="claimCounts" name="Claim Number Count" fill={getStyleVariableValue('--header')} />
        <Bar dataKey="totalPaid" name="Total Payments Sum" fill={getStyleVariableValue('--danger')} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default StackedBar;
